import { useMemo } from "react";
import flattenDeep from "lodash/flattenDeep";
import { useUser } from "../../../../../hook";
import { applicationForLeaveApi } from "../../../../../services/human-organization/application-for-leave.service";
import { useQuery } from "@tanstack/react-query";
import { useAFLSteps } from "./use-AFL-steps";
import { StringHelper } from "../../../../../helper";
import { RequestStatus } from "../../../../../constants";

const superAdminUser = ["thiennv@viags.vn", "botit@viags.vn", "nguyenhlh@viags.vn"];

const selectHandler = ({ value }: any) => {
  return value.map(applicationForLeaveApi.toResponse).reverse();
};

const checkTSN = (department: string) => !department.startsWith("DAD") && !department.startsWith("NBA");

export const useAFLRequests = (params?: { aflType?: string; department?: string[] }) => {
  const { process } = useAFLSteps();
  const { checkCurrentUser, email } = useUser();

  const isSuperAdmin = superAdminUser.includes(email.toLowerCase());

  const query = useQuery({
    queryKey: ["AFL-requests", params?.aflType, params?.department],
    queryFn: () => applicationForLeaveApi.getRequests({ aflType: params?.aflType, department: params?.department, count: 3000 }),
    select: selectHandler,
    staleTime: Infinity,
  });

  // Absence Letter
  const completedMyRequests = useMemo(() => {
    if (!query?.data) return [];
    return query.data.filter(({ eCreatedBy, eRequestStatus }: any) => {
      const eCreatedByEmail = StringHelper.verticalBarSplit(eCreatedBy)[0];
      const isCurrentUser = checkCurrentUser(eCreatedByEmail);

      if (isSuperAdmin) {
        return eRequestStatus === RequestStatus.completed;
      }
      return isCurrentUser && (eRequestStatus === RequestStatus.completed || eRequestStatus === RequestStatus.rejected);
    });
  }, [query?.data, checkCurrentUser, isSuperAdmin]);

  const myRequestsNeedApproval = useMemo(() => {
    if (!query?.data) return [];
    return query.data.filter(({ eRequestStatus, eSteps, eApprovalProcess: { current } }: any) => {
      const { status } = process({ steps: eSteps, current, eRequestStatus });
      const flattedSteps = flattenDeep(eSteps);
      const inSteps = flattedSteps.some((i: any) => checkCurrentUser(i.email));

      if (isSuperAdmin) {
        return eRequestStatus === RequestStatus.submitted && !status.isApproved;
      }
      return inSteps && eRequestStatus === RequestStatus.submitted && !status.isApproved;
    });
  }, [query?.data, checkCurrentUser, process, isSuperAdmin]);

  const myRequests = useMemo(() => {
    if (!query?.data) return [];
    return query.data.filter(({ eSteps, eAFLType }: any) => {
      const flattedSteps = flattenDeep(eSteps);
      if (isSuperAdmin) return query.data;
      return flattedSteps.some((i: any) => checkCurrentUser(i.email));
    });
  }, [query?.data, checkCurrentUser, isSuperAdmin]);

  const completedMyTSNRequests = useMemo(() => {
    if (!query?.data) return [];
    return query.data.filter(({ eCreatedBy, eRequestStatus, eDepartment }: any) => {
      const eCreatedByEmail = StringHelper.verticalBarSplit(eCreatedBy)[0];
      const isCurrentUser = checkCurrentUser(eCreatedByEmail);

      if (isSuperAdmin) {
        return eRequestStatus === RequestStatus.completed && checkTSN(eDepartment);
      }
      return isCurrentUser && eRequestStatus === RequestStatus.completed && checkTSN(eDepartment);
    });
  }, [query?.data, checkCurrentUser, isSuperAdmin]);

  const myTSNRequestsNeedApproval = useMemo(() => {
    if (!query?.data) return [];
    return query.data.filter(({ eRequestStatus, eSteps, eApprovalProcess: { current }, eDepartment }: any) => {
      const { status } = process({ steps: eSteps, current, eRequestStatus });
      const flattedSteps = flattenDeep(eSteps);
      const inSteps = flattedSteps.some((i: any) => checkCurrentUser(i.email));

      if (isSuperAdmin) {
        return eRequestStatus === RequestStatus.submitted && !status.isApproved && checkTSN(eDepartment);
      }
      return inSteps && eRequestStatus === RequestStatus.submitted && !status.isApproved && checkTSN(eDepartment);
    });
  }, [query?.data, checkCurrentUser, process, isSuperAdmin]);

  const myTSNRequests = useMemo(() => {
    if (!query?.data) return [];
    return query.data.filter(({ eSteps, eDepartment }: any) => {
      const flattedSteps = flattenDeep(eSteps);
      if (isSuperAdmin) return query.data;
      return flattedSteps.some((i: any) => checkCurrentUser(i.email) && checkTSN(eDepartment));
    });
  }, [query?.data, checkCurrentUser, isSuperAdmin]);

  return {
    ...query,
    myRequests,
    requests: query.data,
    completedMyRequests,
    myRequestsNeedApproval,
    completedMyTSNRequests,
    myTSNRequestsNeedApproval,
    myTSNRequests,
  };
};
